// Copyright (c) 2024-present, Dinocollab Technologies, Inc. and its affiliates. All rights reserved.

// imports
import { Box, Grid } from '@mui/material'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMergedConfig } from './hooks'
import { ViewGridStyled, viewGridClasses as classes } from './styleds'
import { DEFAULT_GRID_ITEM_HEIGHT_ESTIMATE, resolveGridColumnsFromWidth, resolveGridSpacing } from './helpers'
// types
import type { BoxProps } from '@mui/material'
import type { ComponentType, FC } from 'react'
import type { LoadingModeRule, TPagination } from '../types'
import type { IViewGridConfig, IViewGridProps } from './types'
// re-exports
export * from './types'

//#region Normal
/**
 * Creates a Grid view component that renders all items in the DOM. Suitable for small datasets.
 * @param config - The configuration object for the grid view, containing default settings and required properties.
 * @returns A React functional component that renders a grid view based on the provided configuration and props.
 */
function createViewGridNormal<T>(config: IViewGridConfig<T>) {
  const ViewGridNormal: FC<IViewGridProps<T>> = (props) => {
    const [isScrolling, setIsScrolling] = useState(false)
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)

    const mergedConfig = useMergedConfig<T>(props, config)
    const ItemComponent = mergedConfig.Component
    const isAutoHeight = mergedConfig.normalOptions?.autoHeight === true
    const isClient = props.featureMode === 'client'
    const finalItems = useMemo(() => props.value, [props.value])

    // Reset scroll to top when token changes (client-side filter reset)
    useEffect(() => {
      if (!isClient || isAutoHeight || props.scrollResetToken === undefined) return
      if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0
    }, [isClient, isAutoHeight, props.scrollResetToken])

    const handleScroll: BoxProps['onScroll'] = (e) => {
      const t = e.currentTarget
      setIsScrolling(t.scrollTop > mergedConfig.scrollingThreshold)
      if (t.scrollHeight - t.scrollTop - t.clientHeight <= mergedConfig.nearEndThreshold) {
        props.onNearEnd?.()
      }
    }

    const rootClasses = useMemo(() => {
      const l = [classes.root, classes.normal]
      if (isScrolling) l.push(classes.scrolling)
      if (isAutoHeight) l.push(classes.autoHeight)
      return l.join(' ')
    }, [isScrolling, isAutoHeight])

    return (
      <ViewGridStyled className={rootClasses}>
        <Box className={classes.scrollContainer} ref={scrollContainerRef} onScroll={handleScroll}>
          <Grid container spacing={`${mergedConfig.spacing}px`} className={classes.grid}>
            {finalItems.map((item, index) => {
              const key = config.getterId(item, index) ?? index
              return (
                <Grid key={key} className={classes.gridItem} item {...mergedConfig.sizes}>
                  <ItemComponent value={item} index={index} />
                </Grid>
              )
            })}
          </Grid>
        </Box>
      </ViewGridStyled>
    )
  }
  return ViewGridNormal
}
//#endregion
//#region Virtualized
/**
 * Creates a Grid view component that virtualizes items, rendering only those visible in the viewport. Suitable for large datasets.
 * @param config - The configuration object for the grid view, containing default settings and required properties.
 * @returns A React functional component that renders a virtualized grid view based on the provided configuration and props.
 */
function createViewGridVirtualized<T>(config: IViewGridConfig<T>) {
  const ViewGridVirtualized: FC<IViewGridProps<T>> = (props) => {
    const finalItems = useMemo(() => props.value, [props.value])

    const mergedConfig = useMergedConfig<T>(props, config)
    const { sizes, virtualizedOptions } = mergedConfig
    const ItemComponent = mergedConfig.Component

    // Track container geometry and the first measured item height used for virtualization math.
    const wrapRef = useRef<HTMLDivElement | null>(null)
    const measuredItemsRef = useRef(new Map<number, HTMLDivElement>())
    const measurementRafRef = useRef<number | null>(null)
    // Refs for RAF-throttled scroll: scrollRafRef guards duplicate frames, latestScrollTopRef
    // always holds the most recent scrollTop so the RAF reads the freshest value even if
    // intermediate scroll events were skipped.
    const scrollRafRef = useRef<number | null>(null)
    const latestScrollTopRef = useRef(0)
    const isScrollingRef = useRef(false)
    // windowedRef / measurementEndIndexRef let scheduleMeasuredHeightUpdate read the current
    // window without capturing it in a closure, so the callback never needs to be recreated.
    const windowedRef = useRef({ startIndex: 0, endIndex: -1 })
    const measurementEndIndexRef = useRef(-1)
    // lastFiredScrollTopRef tracks the DOM scrollTop at the last actual setScrollTopState call.
    // The skip check compares both sides in DOM-coordinate space so non-scroll renders (e.g.
    // after measurement) cannot reset the reference point into a stale state-coordinate value.
    const lastFiredScrollTopRef = useRef(0)
    const hasMeasuredDatasetRef = useRef(false)
    const [containerWidth, setContainerWidth] = useState(0)
    const [containerHeight, setContainerHeight] = useState(0)
    const [scrollTopState, setScrollTopState] = useState(0)
    const [isScrolling, setIsScrolling] = useState(false)
    const [measuredItemHeight, setMeasuredItemHeight] = useState<number | null>(null)

    const overscan = Math.max(1, virtualizedOptions.overscan)
    const spacingPx = Math.round(resolveGridSpacing(mergedConfig.spacing))
    const effectiveItemHeight = measuredItemHeight ?? DEFAULT_GRID_ITEM_HEIGHT_ESTIMATE

    // Reset scroll to top when token changes (client-side filter reset)
    useEffect(() => {
      if (props.featureMode !== 'client' || props.scrollResetToken === undefined) return
      // Cancel any pending scroll RAF before resetting so it cannot overwrite the reset state.
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current)
        scrollRafRef.current = null
      }
      if (wrapRef.current) wrapRef.current.scrollTop = 0
      latestScrollTopRef.current = 0
      lastFiredScrollTopRef.current = 0
      setScrollTopState(0)
      if (isScrollingRef.current) {
        isScrollingRef.current = false
        setIsScrolling(false)
      }
    }, [props.featureMode, props.scrollResetToken])

    // Keep viewport dimensions in sync so column count and window size react to container resize.
    useLayoutEffect(() => {
      if (!wrapRef.current) return

      const updateSize = () => {
        const nextWidth = wrapRef.current?.clientWidth ?? 0
        const nextHeight = wrapRef.current?.clientHeight ?? 0
        setContainerWidth((prev) => (prev === nextWidth ? prev : nextWidth))
        setContainerHeight((prev) => (prev === nextHeight ? prev : nextHeight))
      }

      updateSize()

      const observer = new ResizeObserver(() => updateSize())
      observer.observe(wrapRef.current)
      return () => observer.disconnect()
    }, [])

    const columns = resolveGridColumnsFromWidth(containerWidth, sizes)
    const rowHeight = effectiveItemHeight + spacingPx
    // Refs for window-change detection — updated on every render so the handleScroll RAF reads fresh values
    // without needing to re-create the callback. The initial value is overwritten immediately on first render.
    const rowHeightRef = useRef(rowHeight)
    rowHeightRef.current = rowHeight
    const overscanRef = useRef(overscan)
    overscanRef.current = overscan

    // Convert scroll position into a virtual row window plus spacer heights above and below.
    const windowed = useMemo(() => {
      const totalItems = finalItems.length
      if (totalItems === 0) {
        return { startRow: 0, endRow: -1, topSpacerHeight: 0, bottomSpacerHeight: 0, startIndex: 0, endIndex: -1 }
      }

      const totalRows = Math.ceil(totalItems / columns)
      const safeViewportHeight = Math.max(1, containerHeight)
      const effectiveScrollTop = scrollTopState
      let startRow = Math.max(0, Math.floor(effectiveScrollTop / rowHeight) - overscan)
      const visibleRows = Math.ceil(safeViewportHeight / rowHeight)
      let endRow = Math.min(totalRows - 1, startRow + visibleRows + overscan * 2)
      // NOTE: The bottom-clamp that previously pinned startRow when endRow===totalRows-1
      // has been removed. That clamp decoupled startRow from scrollTopState, causing a
      // visual jump when loadMore added items (clamp released → startRow changed even
      // though scrollTopState was unchanged). Total virtual height equals totalRows*rowHeight
      // regardless of startRow, so there is no oscillation feedback loop without the clamp.
      const startIndex = startRow * columns
      const endIndex = Math.min(totalItems - 1, (endRow + 1) * columns - 1)
      const topSpacerHeight = startRow * rowHeight
      const bottomSpacerHeight = Math.max(0, (totalRows - endRow - 1) * rowHeight)

      return { startRow, endRow, topSpacerHeight, bottomSpacerHeight, startIndex, endIndex }
    }, [columns, finalItems.length, overscan, rowHeight, scrollTopState, containerHeight])

    // Keep refs in sync so RAF callbacks and scheduleMeasuredHeightUpdate read the current
    // window without capturing it in a closure (avoids recreating those callbacks on scroll).
    windowedRef.current = windowed
    measurementEndIndexRef.current = windowed.endIndex

    const visibleItems = useMemo(
      () => (windowed.endIndex >= windowed.startIndex ? finalItems.slice(windowed.startIndex, windowed.endIndex + 1) : []),
      [finalItems, windowed.startIndex, windowed.endIndex]
    )
    const measurementEndIndex = windowed.endIndex
    const contentSx = useMemo<BoxProps['sx']>(() => {
      return {
        display: 'grid',
        gap: `${spacingPx}px`,
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
      }
    }, [columns, spacingPx])

    // Memoize the item-height sx object so MUI doesn't re-process styles on every render.
    const gridItemSx = useMemo<BoxProps['sx']>(
      () => (measuredItemHeight !== null ? { minHeight: effectiveItemHeight, height: effectiveItemHeight } : undefined),
      [measuredItemHeight, effectiveItemHeight]
    )

    const handleScroll: BoxProps['onScroll'] = (e) => {
      const t = e.currentTarget
      const newScrollTop = Math.round(t.scrollTop)
      latestScrollTopRef.current = newScrollTop

      // nearEnd check is urgent — fire immediately outside RAF
      if (t.scrollHeight - t.scrollTop - t.clientHeight <= mergedConfig.nearEndThreshold) {
        props.onNearEnd?.()
      }

      // Throttle virtualization state updates to one per animation frame.
      // Multiple scroll events in the same frame are collapsed into one RAF.
      if (scrollRafRef.current !== null) return
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null
        // Always read the real DOM scrollTop inside the RAF — do NOT rely on latestScrollTopRef.
        // latestScrollTopRef can be stale if browser scroll-anchoring silently adjusts scrollTop
        // (e.g. when bottomSpacer grows after loadMore) without firing a new scroll event.
        const domScrollTop = Math.round(wrapRef.current?.scrollTop ?? latestScrollTopRef.current)
        latestScrollTopRef.current = domScrollTop

        // Only setState for isScrolling when the boolean value actually flips.
        const nextIsScrolling = domScrollTop > mergedConfig.scrollingThreshold
        if (nextIsScrolling !== isScrollingRef.current) {
          isScrollingRef.current = nextIsScrolling
          setIsScrolling(nextIsScrolling)
        }

        // Skip scroll state update if the virtual window (startRow) wouldn't change.
        // Both sides are computed from DOM-coordinate values (lastFiredScrollTopRef and domScrollTop)
        // so non-scroll renders (e.g. after measurement) cannot reset the dead zone into a stale state.
        const lastFiredStartRow = Math.max(0, Math.floor(lastFiredScrollTopRef.current / rowHeightRef.current) - overscanRef.current)
        const newStartRow = Math.max(0, Math.floor(domScrollTop / rowHeightRef.current) - overscanRef.current)
        if (newStartRow === lastFiredStartRow) return

        lastFiredScrollTopRef.current = domScrollTop
        setScrollTopState(domScrollTop)
      })
    }

    const cancelScheduledMeasurement = useCallback(() => {
      if (measurementRafRef.current === null) return
      cancelAnimationFrame(measurementRafRef.current)
      measurementRafRef.current = null
    }, [])

    // Measure the largest item in the first rendered window once, then reuse it permanently.
    // Reads windowed bounds from refs so this callback is stable (deps=[]) and never recreated.
    const scheduleMeasuredHeightUpdate = useCallback(() => {
      if (hasMeasuredDatasetRef.current || measurementRafRef.current !== null) return

      measurementRafRef.current = requestAnimationFrame(() => {
        measurementRafRef.current = null
        if (hasMeasuredDatasetRef.current) return

        const { startIndex } = windowedRef.current
        const endIndex = measurementEndIndexRef.current
        const elements = Array.from(measuredItemsRef.current.entries())
          .filter(([index]) => index >= startIndex && index <= endIndex)
          .map(([, element]) => element)

        if (elements.length === 0) return

        let nextHeight = 0
        for (const element of elements) {
          nextHeight = Math.max(nextHeight, element.getBoundingClientRect().height)
        }

        if (nextHeight <= 0) return

        hasMeasuredDatasetRef.current = true
        setMeasuredItemHeight(Math.round(nextHeight))
      })
    }, [])

    const setMeasuredItemRef = useCallback((index: number, node: HTMLDivElement | null) => {
      if (node) {
        measuredItemsRef.current.set(index, node)
        return
      }

      measuredItemsRef.current.delete(index)
    }, [])

    useLayoutEffect(() => {
      // Once measured, the effect is a cheap no-op on every subsequent scroll.
      if (hasMeasuredDatasetRef.current) return

      const elements = Array.from(measuredItemsRef.current.entries())
        .filter(([index]) => index >= windowed.startIndex && index <= measurementEndIndex)
        .map(([, element]) => element)

      if (elements.length === 0) return

      scheduleMeasuredHeightUpdate()

      const observer = new ResizeObserver(() => {
        scheduleMeasuredHeightUpdate()
      })

      for (const element of elements) {
        observer.observe(element)
      }

      return () => {
        observer.disconnect()
        cancelScheduledMeasurement()
      }
    }, [windowed.startIndex, measurementEndIndex, scheduleMeasuredHeightUpdate, cancelScheduledMeasurement])

    useLayoutEffect(() => {
      return () => {
        cancelScheduledMeasurement()
      }
    }, [cancelScheduledMeasurement])

    // Cancel any pending scroll RAF on unmount to prevent setState on an unmounted component.
    useEffect(() => {
      return () => {
        if (scrollRafRef.current !== null) {
          cancelAnimationFrame(scrollRafRef.current)
          scrollRafRef.current = null
        }
      }
    }, [])

    const rootClasses = [classes.root, classes.virtualized]
    if (isScrolling) rootClasses.push(classes.scrolling)
    return (
      <ViewGridStyled className={rootClasses.filter(Boolean).join(' ')}>
        <Box ref={wrapRef} className={classes.scrollContainer} onScroll={handleScroll}>
          {/* Spacer keeps total scroll height stable for rows rendered before the current window. */}
          {windowed.topSpacerHeight > 0 ? <Box sx={{ height: windowed.topSpacerHeight }} /> : null}

          <Box sx={contentSx} className={classes.grid}>
            {visibleItems.map((item, offset) => {
              const index = windowed.startIndex + offset
              const key = config.getterId(item, index) ?? index
              return (
                <Box
                  key={key}
                  className={classes.gridItem}
                  ref={!hasMeasuredDatasetRef.current ? (node) => setMeasuredItemRef(index, node as HTMLDivElement | null) : undefined}
                  sx={gridItemSx}
                >
                  <ItemComponent value={item} index={index} />
                </Box>
              )
            })}
          </Box>

          {/* Spacer keeps total scroll height stable for rows rendered after the current window. */}
          {windowed.bottomSpacerHeight > 0 ? <Box sx={{ height: windowed.bottomSpacerHeight }} /> : null}
        </Box>
      </ViewGridStyled>
    )
  }
  return ViewGridVirtualized
}
//#endregion
//#region Main
/**
 * Factory function to create a Grid view component that can switch between normal and virtualized rendering strategies based on props and configuration.
 * @param config - The configuration object for the grid view, containing default settings and required properties.
 * @returns A React functional component that renders a grid view based on the provided configuration and props, supporting both normal and virtualized rendering strategies.
 */
export function createViewGrid<T>(config: IViewGridConfig<T>) {
  const ViewGridNormal = createViewGridNormal<T>(config)
  const ViewGridVirtualized = createViewGridVirtualized<T>(config)

  const ViewGrid: FC<IViewGridProps<T>> = (props) => {
    const renderStrategy = props.renderStrategy ?? config.renderStrategy ?? 'normal'
    if (renderStrategy === 'virtualized') {
      return <ViewGridVirtualized {...props} />
    }
    return <ViewGridNormal {...props} />
  }
  return ViewGrid
}

export interface IViewGridLoadingProps<T> extends IViewGridProps<T> {
  value: T[]
  loadMode: LoadingModeRule
  loading?: boolean
  pagination?: TPagination
  onPageChange?: (page: number, pageSize: number) => void | Promise<void>
}

export function createViewGridLoading<T>(GridComponent: ComponentType<IViewGridProps<T>>) {
  const ViewGridLoading: FC<IViewGridLoadingProps<T>> = (props) => {
    // Guard against duplicate calls during the window between onPageChange and loading=true propagating back
    const pendingRef = useRef(false)

    // Sync pendingRef when loading resets externally (e.g. error path where onPageChange didn't resolve)
    useEffect(() => {
      if (!props.loading) pendingRef.current = false
    }, [props.loading])

    const handleNearEnd = useCallback(async () => {
      if (props.loadMode !== 'infiniteScroll') return
      if (!props.pagination?.hasNext) return
      if (props.loading) return
      if (pendingRef.current) return

      pendingRef.current = true
      try {
        const currentPage = props.pagination?.page ?? 0
        const currentPageSize = props.pagination?.pageSize ?? 20
        const nextPage = currentPage + 1
        await props.onPageChange?.(nextPage, currentPageSize)
      } finally {
        pendingRef.current = false
      }
    }, [props.loadMode, props.pagination?.hasNext, props.pagination?.page, props.pagination?.pageSize, props.loading, props.onPageChange])

    return (
      <GridComponent
        value={props.value}
        spacing={props.spacing}
        sizes={props.sizes}
        renderStrategy={props.renderStrategy}
        Component={props.Component}
        normalOptions={props.normalOptions}
        virtualizedOptions={props.virtualizedOptions}
        onNearEnd={props.loadMode === 'infiniteScroll' ? handleNearEnd : undefined}
        nearEndThreshold={props.nearEndThreshold}
        scrollResetToken={props.scrollResetToken}
      />
    )
  }

  return ViewGridLoading
}

export default createViewGrid
//#endregion
